import React, { useState } from 'react';
import { signIn, signUp, resetPassword, signInAsGuest } from '../firebase/auth';
import { mapAuthError } from '../utils/userFacingErrors';
import { PUBLIC_SITE_URL } from '../utils/api';
import Icon from './Icon';
import { AprLockup } from './BrandMarks';
import './AuthForm.css';

/**
 * Sign in / create account / reset password. In the public app it floats
 * over the conversation (nothing is lost by signing up); in the team portal
 * (`portal`) it is the front door and has no guest option.
 */
function AuthForm({ onContinueAsGuest, onSignedIn, portal = false }) {
  const [mode, setMode] = useState(portal ? 'signin' : 'signup'); // signin | signup | reset
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError('');
    setResetSuccess(false);
    setPassword('');
    setConfirmPassword('');
    if (nextMode !== 'signup') {
      setName('');
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email.trim(), password);
      if (onSignedIn) onSignedIn();
    } catch (err) {
      setError(mapAuthError(err, 'signIn'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Please enter your first name.');
      return;
    }
    if (password.length < 6) {
      setError('Use a password with at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await signUp(email.trim(), password, trimmedName);
      localStorage.setItem('accountJustCreated', 'true');
      // Guest upgrades link in place (same uid), which doesn't always fire
      // the auth listener — tell the app directly.
      if (onSignedIn) onSignedIn();
    } catch (err) {
      setError(mapAuthError(err, 'signUp'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setResetSuccess(false);

    try {
      await resetPassword(email.trim());
      setResetSuccess(true);
      setError('');
    } catch (err) {
      setError(mapAuthError(err, 'reset'));
    } finally {
      setLoading(false);
    }
  };

  const handleContinueAsGuest = async () => {
    setError('');
    // A guest session may already exist (user opened this form from the app);
    // otherwise start one.
    if (onContinueAsGuest) {
      onContinueAsGuest();
      return;
    }
    setLoading(true);
    try {
      await signInAsGuest();
    } catch (err) {
      setError("We couldn't start a guest session. Please try again.");
      setLoading(false);
    }
  };

  const formTitle =
    mode === 'reset' ? 'Reset your password' :
    mode === 'signup' ? 'Create a free account' :
    portal ? 'Sign in to the team portal' : 'Welcome back';

  const onSubmit =
    mode === 'reset' ? handlePasswordReset :
    mode === 'signup' ? handleSignUp :
    handleSignIn;

  // The policy pages live on the public site; on the portal's own host the
  // same paths would just open the portal again.
  const policyHref = (path) => (portal ? `${PUBLIC_SITE_URL}${path}` : path);

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title"><AprLockup height={24} label="Ask Pastor Rob" /></h1>
          <p className="auth-subtitle">{portal ? 'Team portal · The Net, downtown Huntsville' : 'AI-assisted answers from Rob’s sermons · The Net, Huntsville'}</p>
        </div>

        {mode !== 'reset' && (
          <div className="auth-mode-toggle" role="tablist" aria-label="Account options">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'signin'}
              className={`auth-mode-btn ${mode === 'signin' ? 'active' : ''}`}
              onClick={() => switchMode('signin')}
            >
              {mode === 'signin' && <Icon name="check" size={16} />}
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'signup'}
              className={`auth-mode-btn ${mode === 'signup' ? 'active' : ''}`}
              onClick={() => switchMode('signup')}
            >
              {mode === 'signup' && <Icon name="check" size={16} />}
              Create account
            </button>
          </div>
        )}

        <form onSubmit={onSubmit} className="auth-form">
          <h2 className="form-title">{formTitle}</h2>
          {mode === 'signup' && !portal && (
            <p className="auth-benefits">Keep your conversations, ask more questions, and let Rob call you by name. Free, always.</p>
          )}

          {resetSuccess ? (
            <div className="success-message" role="status">
              <Icon name="check" size={20} />
              <div>
                <p>If an account exists for that email, reset instructions are on their way.</p>
                <p>Check your inbox (and spam folder), then come back and sign in.</p>
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={() => switchMode('signin')}
                >
                  Back to sign in
                </button>
              </div>
            </div>
          ) : (
            <>
              {mode === 'signup' && (
                <div className="form-group">
                  <label htmlFor="name" className="form-label">First name</label>
                  <input
                    id="name"
                    type="text"
                    className="input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="What should Rob call you?"
                    autoComplete="given-name"
                    required
                    maxLength={40}
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  id="email"
                  type="email"
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  inputMode="email"
                  required
                />
              </div>

              {mode !== 'reset' && (
                <div className="form-group">
                  <label htmlFor="password" className="form-label">Password</label>
                  <input
                    id="password"
                    type="password"
                    className="input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === 'signup' ? 'At least 6 characters' : 'Your password'}
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    required
                    minLength={6}
                  />
                </div>
              )}

              {mode === 'signup' && (
                <div className="form-group">
                  <label htmlFor="confirmPassword" className="form-label">Confirm password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    className="input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    required
                    minLength={6}
                  />
                </div>
              )}

              {error && (
                <div className="error-message apr-error" role="alert">
                  <Icon name="triangle-alert" size={16} />
                  <span>{error}</span>
                </div>
              )}

              {mode === 'reset' && (
                <p className="reset-info">
                  We'll email you a link to choose a new password.
                </p>
              )}

              {/* Right above the button it describes: pressing it is the agreement. */}
              {mode !== 'reset' && !portal && (
                <p className="auth-consent">
                  By continuing, you agree to our{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>
                  {' '}and acknowledge our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </p>
              )}

              <button
                type="submit"
                className="apr-btn apr-btn--block auth-submit"
                disabled={loading}
              >
                {loading ? (
                  <span className="loading-text">
                    {mode === 'reset' ? 'Sending…' :
                      mode === 'signup' ? 'Creating your account…' :
                      'Signing in…'}
                  </span>
                ) : (
                  mode === 'reset' ? 'Send reset email' :
                  mode === 'signup' ? 'Create account' :
                  'Sign in'
                )}
              </button>

              <div className="auth-links">
                {mode === 'signin' && (
                  <button
                    type="button"
                    className="btn btn-link forgot-password"
                    onClick={() => switchMode('reset')}
                  >
                    Forgot password?
                  </button>
                )}
                {mode === 'reset' && (
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => switchMode('signin')}
                  >
                    Back to sign in
                  </button>
                )}
                {mode === 'signup' && (
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => switchMode('signin')}
                  >
                    Already have an account? Sign in
                  </button>
                )}
              </div>
            </>
          )}
        </form>

        {mode !== 'reset' && !portal && (
          <div className="auth-guest">
            <button
              type="button"
              className="btn btn-link guest-continue"
              onClick={handleContinueAsGuest}
              disabled={loading}
            >
              {onContinueAsGuest ? 'Keep going as a guest' : 'Continue as a guest. No account needed.'}
            </button>
            <p className="guest-hint">
              Guests can ask a few questions every few hours. Accounts are free and keep your conversations in your list, with more questions.
            </p>
          </div>
        )}

        <div className="auth-footer">
          <p className="footer-text auth-privacy-note">
            Your conversations are private. We never sell them, and we don’t share
            them with advertisers or anyone outside the services that run Ask
            Pastor Rob.
          </p>
          <p className="footer-text auth-policy-links">
            <a href={policyHref('/privacy')} target="_blank" rel="noopener noreferrer">Privacy</a>
            <span aria-hidden="true"> · </span>
            <a href={policyHref('/terms')} target="_blank" rel="noopener noreferrer">Terms</a>
            <span aria-hidden="true"> · </span>
            <a href={policyHref('/crisis-help')} target="_blank" rel="noopener noreferrer">Crisis resources</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default AuthForm;
